import * as XLSX from "xlsx";

/**
 * Results workbook in the layout ITRA accepts.
 *
 * ITRA reads these files by column position and by code, not by the labels a
 * timing system happens to use, so their own reference sheet defines what is
 * correct here rather than our internal field names: M/F rather than
 * male/female, three-letter country codes rather than full country names, one
 * sheet per category, and the family name split out of the single name field
 * we store.
 */

// ISO 3166-1 alpha-3. Keyed on what entry lists actually contain, including
// the spellings runners type themselves — "GERMAN" for Germany, and England,
// Scotland and Wales all resolving to GBR.
const ISO3 = {
  INDONESIA: "INA", INA: "INA",
  FRANCE: "FRA", AUSTRALIA: "AUS",
  GERMANY: "GER", GERMAN: "GER",
  MALAYSIA: "MAS", NETHERLANDS: "NED", SWEDEN: "SWE", SPAIN: "ESP",
  NORWAY: "NOR", SWITZERLAND: "SUI", "CZECH REPUBLIC": "CZE",
  LUXEMBOURG: "LUX", RUSSIA: "RUS",
  ENGLAND: "GBR", "UNITED KINGDOM": "GBR", SCOTLAND: "GBR", WALES: "GBR",
  CHILE: "CHI", VENEZUELA: "VEN",
  "UNITED STATES": "USA", USA: "USA", CANADA: "CAN", ITALY: "ITA",
  SINGAPORE: "SGP", JAPAN: "JPN", "SOUTH KOREA": "KOR", CHINA: "CHN",
  INDIA: "IND", THAILAND: "THA", PHILIPPINES: "PHI", VIETNAM: "VIE",
  "NEW ZEALAND": "NZL", BELGIUM: "BEL", AUSTRIA: "AUT", POLAND: "POL",
  DENMARK: "DEN", FINLAND: "FIN", IRELAND: "IRL", PORTUGAL: "POR",
  BRAZIL: "BRA", MEXICO: "MEX", ARGENTINA: "ARG", "SOUTH AFRICA": "RSA",
};

const toIso3 = (value, unmapped) => {
  const key = String(value || "").trim().toUpperCase();
  if (!key || key === "N/A") return "";
  if (ISO3[key]) return ISO3[key];
  if (/^[A-Z]{3}$/.test(key)) return key; // already a code
  unmapped?.add(key);
  return key;
};

const toMF = (gender) => {
  const key = String(gender || "").trim().toLowerCase();
  if (key.startsWith("m")) return "M";
  if (key.startsWith("f")) return "F";
  return "";
};

/**
 * Split our single `name` field the way the reference sheet does: the last
 * word is the family name and the rest is the first name. A one-word name goes
 * entirely into First Name with the family column left empty — that is how
 * "HANDRIAWAN" appears there, not as a family name with no given name.
 */
const splitName = (full) => {
  const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { family: "", first: "" };
  if (parts.length === 1) return { family: "", first: parts[0] };
  return { family: parts[parts.length - 1], first: parts.slice(0, -1).join(" ") };
};

// Milliseconds exist for ranking, not for a federation submission.
const hhmmss = (v) => (typeof v === "string" ? v.replace(/\.\d+$/, "") : "");

const HEADER = ["Ranking", "Time", "Family Name", "First Name", "Gender", "Birthdate", "Nationality"];

/**
 * @param {Array}  results    Every result row for the event (all pages).
 * @param {Array}  categories Event categories, for sheet names and order.
 * @param {string} eventId    Used for the filename.
 * @returns {{sheets: Array, unmapped: string[]}} Per-category counts, so the
 *          caller can warn about blanks before the file is submitted.
 */
export const generateAndDownloadItraXlsx = (results, categories, eventId) => {
  if (!results || results.length === 0) throw new Error("No results to export");

  const wb = XLSX.utils.book_new();
  const unmapped = new Set();
  const sheets = [];

  for (const cat of categories || []) {
    const rows = results
      .filter((r) => r.categoryId === cat.id)
      // A submission lists finishers. Anyone without a placing — DNF, DNS, or
      // over the cut-off — is not a result, and a row with no time is rejected.
      .filter((r) => r.overallRank > 0 && (r.finishDuration || r.finishTime))
      .sort((a, b) => a.overallRank - b.overallRank)
      .map((r) => {
        const { family, first } = splitName(r.name);
        return {
          Ranking: r.overallRank,
          Time: hhmmss(r.finishDuration || r.finishTime),
          "Family Name": family,
          "First Name": first,
          Gender: toMF(r.gender),
          Birthdate: r.birthDate || "",
          Nationality: toIso3(r.nationality, unmapped),
        };
      });

    if (rows.length === 0) continue;

    const ws = XLSX.utils.json_to_sheet(rows, { header: HEADER });
    ws["!cols"] = [{ wch: 9 }, { wch: 10 }, { wch: 26 }, { wch: 24 }, { wch: 8 }, { wch: 12 }, { wch: 12 }];
    // The format itself caps sheet names at 31 characters.
    XLSX.utils.book_append_sheet(wb, ws, String(cat.name).slice(0, 31));

    sheets.push({
      name: cat.name,
      count: rows.length,
      noBirthdate: rows.filter((r) => !r.Birthdate).length,
      noNationality: rows.filter((r) => !r.Nationality).length,
    });
  }

  if (sheets.length === 0) throw new Error("No ranked finishers to export");

  XLSX.writeFile(wb, `ITRA_${eventId || "event"}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  return { sheets, unmapped: [...unmapped] };
};

export default generateAndDownloadItraXlsx;
